import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import { Flower2, Plus, Edit2, Trash2, Users, UserPlus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRAINING_TYPES = [
  { id: 'modern_farming', label: 'Modern Farming' },
  { id: 'business_to_business', label: 'Business to Business' },
  { id: 'agriculture', label: 'Agriculture' },
  { id: 'post_harvest_handling', label: 'Post Harvest Handling' },
  { id: 'other', label: 'Other' },
];

const emptyTraining = { training_type: 'modern_farming', title: '', description: '', instructor: '', start_date: '', end_date: '', location: '', status: 'active' };
const emptyParticipant = { training_id: '', name: '', address: '', contact: '', notes: '' };

const WomenDevelopmentSection = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'project_manager']);

  const [trainings, setTrainings] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);

  const [trDialog, setTrDialog] = useState(false);
  const [trEditing, setTrEditing] = useState(null);
  const [trForm, setTrForm] = useState({ ...emptyTraining });

  const [pDialog, setPDialog] = useState(false);
  const [pEditing, setPEditing] = useState(null);
  const [pForm, setPForm] = useState({ ...emptyParticipant });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API_URL}/api/women-development/trainings`),
        axios.get(`${API_URL}/api/women-development/participants`),
      ]);
      setTrainings(t.data);
      setParticipants(p.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const submitTraining = async (e) => {
    e.preventDefault();
    try {
      if (trEditing) {
        await axios.put(`${API_URL}/api/women-development/trainings/${trEditing.id}`, trForm);
        toast.success('Updated');
      } else {
        await axios.post(`${API_URL}/api/women-development/trainings`, trForm);
        toast.success('Training added');
      }
      setTrDialog(false); setTrEditing(null); setTrForm({ ...emptyTraining });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const submitParticipant = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/women-development/participants`, pForm);
      toast.success('Participant added');
      setPDialog(false); setPForm({ ...emptyParticipant });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteTraining = async (id) => {
    if (!window.confirm('Delete this training and all its participants?')) return;
    await axios.delete(`${API_URL}/api/women-development/trainings/${id}`);
    fetchAll();
  };

  const deleteParticipant = async (id) => {
    if (!window.confirm('Remove this participant?')) return;
    await axios.delete(`${API_URL}/api/women-development/participants/${id}`);
    fetchAll();
  };

  const trainingLabel = (id) => {
    const t = trainings.find(tr => tr.id === id);
    return t ? (t.title || TRAINING_TYPES.find(tt => tt.id === t.training_type)?.label || t.training_type) : '—';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Flower2 className="h-4 w-4 text-pink-600" /> Women for Development — Training Programs</CardTitle>
            <CardDescription>Manage trainings and enrolled participants</CardDescription>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setTrEditing(null); setTrForm({ ...emptyTraining }); setTrDialog(true); }} data-testid="add-training-btn">
                <Plus className="h-4 w-4 mr-1" /> Training
              </Button>
              <Button size="sm" className="bg-pink-600 hover:bg-pink-700" onClick={() => { setPForm({ ...emptyParticipant }); setPDialog(true); }} data-testid="add-women-participant-btn" disabled={trainings.length === 0}>
                <UserPlus className="h-4 w-4 mr-1" /> Participant
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="trainings">
          <TabsList>
            <TabsTrigger value="trainings" data-testid="women-tab-trainings">Trainings ({trainings.length})</TabsTrigger>
            <TabsTrigger value="participants" data-testid="women-tab-participants">Participants ({participants.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="trainings" className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Training Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Instructor</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Participants</TableHead>
                  {canEdit && <TableHead className="w-24"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
                ) : trainings.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No trainings yet</TableCell></TableRow>
                ) : trainings.map((t) => (
                  <TableRow key={t.id} data-testid={`women-training-${t.id}`}>
                    <TableCell><Badge variant="outline" className="capitalize">{t.training_type.replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell className="font-medium">{t.title || '—'}</TableCell>
                    <TableCell>{t.instructor || '—'}</TableCell>
                    <TableCell className="text-sm">
                      {t.start_date ? formatDate(t.start_date) : '—'}
                      {t.end_date && ` → ${formatDate(t.end_date)}`}
                    </TableCell>
                    <TableCell><Badge variant="secondary">{t.participant_count || 0}</Badge></TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button size="icon" variant="ghost" onClick={() => { setTrEditing(t); setTrForm({ ...emptyTraining, ...t }); setTrDialog(true); }}><Edit2 className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteTraining(t.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="participants" className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Training Attended</TableHead>
                  {canEdit && <TableHead className="w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {participants.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No participants enrolled yet</TableCell></TableRow>
                ) : participants.map((p) => (
                  <TableRow key={p.id} data-testid={`women-participant-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.address || '—'}</TableCell>
                    <TableCell>{p.contact || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{p.training_name || trainingLabel(p.training_id)}</Badge></TableCell>
                    {canEdit && (
                      <TableCell>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteParticipant(p.id)}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* Training Dialog */}
      <Dialog open={trDialog} onOpenChange={(o) => { setTrDialog(o); if (!o) { setTrEditing(null); setTrForm({ ...emptyTraining }); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{trEditing ? 'Edit' : 'Add'} Training Program</DialogTitle>
            <DialogDescription>Create a new training under Women for Development</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitTraining} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Training Type *</Label>
              <Select value={trForm.training_type} onValueChange={(v) => setTrForm({ ...trForm, training_type: v })}>
                <SelectTrigger data-testid="training-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TRAINING_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={trForm.title} onChange={(e) => setTrForm({ ...trForm, title: e.target.value })} placeholder="Optional descriptive title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Instructor</Label><Input value={trForm.instructor} onChange={(e) => setTrForm({ ...trForm, instructor: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Location</Label><Input value={trForm.location} onChange={(e) => setTrForm({ ...trForm, location: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Start Date</Label><Input type="date" value={trForm.start_date} onChange={(e) => setTrForm({ ...trForm, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End Date</Label><Input type="date" value={trForm.end_date} onChange={(e) => setTrForm({ ...trForm, end_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={trForm.description} onChange={(e) => setTrForm({ ...trForm, description: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTrDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="training-submit-btn">{trEditing ? 'Update' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Participant Dialog */}
      <Dialog open={pDialog} onOpenChange={(o) => { setPDialog(o); if (!o) setPForm({ ...emptyParticipant }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Participant</DialogTitle>
            <DialogDescription>Enroll a participant in a training program</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitParticipant} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Training Attended *</Label>
              <Select value={pForm.training_id} onValueChange={(v) => setPForm({ ...pForm, training_id: v })}>
                <SelectTrigger data-testid="participant-training-select"><SelectValue placeholder="Choose training" /></SelectTrigger>
                <SelectContent>
                  {trainings.map(t => <SelectItem key={t.id} value={t.id}>{t.title || TRAINING_TYPES.find(tt => tt.id === t.training_type)?.label || t.training_type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Name *</Label><Input value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} required data-testid="participant-name-input" /></div>
            <div className="space-y-1.5"><Label>Address</Label><Input value={pForm.address} onChange={(e) => setPForm({ ...pForm, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contact</Label><Input value={pForm.contact} onChange={(e) => setPForm({ ...pForm, contact: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={pForm.notes} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="participant-submit-btn">Add</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WomenDevelopmentSection;
